import type { Order, TrackingEvent } from './types';

/**
 * Transforms Deleo trace events to UI TrackingEvent format
 */
export const transformTrackingEvents = (order: Order): TrackingEvent[] => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;

  if (!traceList || traceList.length === 0) {
    return [];
  }

  return traceList.map((event, index) => {
    const eventTypeMap: Record<string, string> = {
      D06: 'delivered',
      I04: 'customs_cleared',
      I03: 'in_customs',
      I02: 'arrived_destination',
      I01: 'departed',
      I00: 'invoice_issued',
    };

    const eventStatus = index === 0 ? (event.status === 'D06' ? 'completed' : 'current') : 'completed';

    return {
      id: `${index}-${event.eventTime}`,
      type: eventTypeMap[event.status] || 'other',
      title: event.description,
      timestamp: event.eventTime,
      status: eventStatus as 'completed' | 'current' | 'pending',
    };
  });
};

/**
 * Gets the tracking number from the order
 */
export const getTrackingNumber = (order: Order): string => {
  return order.deleoData?.deleoTrackingNo || '';
};

/**
 * Gets the delivery date from the order tracking events
 */
export const getDeliveryDate = (order: Order): string | undefined => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;
  const deliveredEvent = traceList?.find((event) => event.status === 'D06');

  if (deliveredEvent?.eventTime) {
    const date = new Date(deliveredEvent.eventTime.replace(' ', 'T'));
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return undefined;
};

/**
 * Gets the order date formatted
 */
export const getFormattedOrderDate = (order: Order): string => {
  const date = new Date(order.orderDateTime || order.createdAt);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Transforms Order API response to display format for OrderCard
 */
export const transformOrderForDisplay = (order: Order) => {
  return {
    id: Number(order.orderId),
    orderNumber: `#${order.orderId}`,
    status: order.status,
    orderDate: getFormattedOrderDate(order),
    deliveryDate: getDeliveryDate(order) || 'Pending',
    trackingNumber: getTrackingNumber(order),
    total: order.totalAmount,
    currency: order.currency,
    items: order.lineItems.map((item, index) => ({
      id: index,
      name: item.name,
      imageUrl: item.wooCommerceImageUrl,
      variant: [item.productSize, item.productPack].filter(Boolean).join(' '),
      quantity: item.quantity,
      price: item.price || (item.total && item.quantity ? item.total / item.quantity : 0),
    })),
    trackingEvents: transformTrackingEvents(order),
  };
};

